import { assertSupabase, isSupabaseConfigured, supabase } from "../lib/supabase.js";
import { fallbackBusinesses, fallbackReviews } from "./fallback-data.js";

function byNumberDesc(field) {
  return (left, right) => Number(right[field] || 0) - Number(left[field] || 0);
}

function matchesText(value, query) {
  return String(value || "").toLowerCase().includes(String(query || "").toLowerCase());
}

export function normalizeBusiness(row) {
  const services = row.services || [];
  const firstPrice = services
    .map((service) => Number(service.price_from))
    .filter((price) => Number.isFinite(price) && price > 0)
    .sort((a, b) => a - b)[0] || null;

  return {
    ...row,
    services,
    displayPrice: firstPrice,
    activeServices: services.filter((service) => service.is_active !== false),
  };
}

export function applyBusinessFilters(businesses, filters = {}) {
  let list = businesses.map(normalizeBusiness);
  if (filters.category) list = list.filter((business) => business.category === filters.category);
  if (filters.address) {
    list = list.filter((business) => matchesText(business.address, filters.address) || matchesText(business.name, filters.address));
  }
  if (filters.rating) list = list.filter((business) => Number(business.rating || 0) >= Number(filters.rating));
  if (filters.maxPrice) {
    list = list.filter((business) => !business.displayPrice || business.displayPrice <= Number(filters.maxPrice));
  }
  if (filters.service) {
    list = list.filter((business) => business.activeServices.some((service) => matchesText(service.name, filters.service)));
  }
  if (filters.sort === "rating") list.sort(byNumberDesc("rating"));
  if (filters.sort === "price") list.sort((left, right) => Number(left.displayPrice || 999999) - Number(right.displayPrice || 999999));
  return list;
}

export async function getCurrentSession() {
  if (!isSupabaseConfigured) return { user: null, profile: null, unavailable: true };
  const client = assertSupabase();
  const { data: sessionData, error: sessionError } = await client.auth.getSession();
  if (sessionError) throw sessionError;
  const user = sessionData.session?.user || null;
  if (!user) return { user: null, profile: null };
  let { data: profile, error } = await client.from("profiles").select("*").eq("id", user.id).maybeSingle();
  if (error) throw error;
  if (!profile) {
    profile = await ensureProfile({
      fullName: user.user_metadata?.full_name || user.email?.split("@")[0] || "",
      phone: user.user_metadata?.phone || "",
    });
  }
  return { user, profile };
}

export async function signUpUser({ email, password, fullName, phone, role = "user" }) {
  const client = assertSupabase();
  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: { data: { full_name: fullName, phone, role } },
  });
  if (error) throw error;
  if (data.session?.user) {
    await upsertProfile({ id: data.user.id, fullName, phone, role });
  }
  return data;
}

export async function signInUser({ email, password }) {
  const client = assertSupabase();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOutUser() {
  const client = assertSupabase();
  const { error } = await client.auth.signOut();
  if (error) throw error;
}

export async function upsertProfile({ id, fullName, phone }) {
  const client = assertSupabase();
  const { data, error } = await client.from("profiles").upsert({
    id,
    full_name: fullName,
    phone,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function ensureProfile({ fullName = "", phone = "" } = {}) {
  const client = assertSupabase();
  const { data, error } = await client.rpc("ensure_profile", {
    full_name_input: fullName,
    phone_input: phone,
  });
  if (error) throw error;
  return data;
}

export async function loadBusinesses(filters = {}) {
  if (!isSupabaseConfigured) {
    return { data: applyBusinessFilters(fallbackBusinesses, filters), source: "fallback" };
  }
  try {
    const { data, error } = await supabase
      .from("businesses")
      .select("*, services(*)")
      .eq("is_active", true)
      .order("is_verified", { ascending: false })
      .order("rating", { ascending: false });
    if (error) throw error;
    const rows = Array.isArray(data) && data.length > 0 ? data : fallbackBusinesses;
    return { data: applyBusinessFilters(rows, filters), source: rows === fallbackBusinesses ? "fallback-empty" : "supabase" };
  } catch (error) {
    return { data: applyBusinessFilters(fallbackBusinesses, filters), source: "fallback", error };
  }
}

export async function loadBusinessServices(businessId) {
  const client = assertSupabase();
  const { data, error } = await client.from("services").select("*").eq("business_id", businessId).eq("is_active", true).order("name");
  if (error) throw error;
  return data || [];
}

export async function loadUserCars(userId) {
  const client = assertSupabase();
  const { data, error } = await client.from("cars").select("*").eq("user_id", userId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createCar(userId, car) {
  const client = assertSupabase();
  const { data, error } = await client.from("cars").insert({
    user_id: userId,
    brand: car.brand,
    model: car.model,
    year: car.year || null,
    wheel_size: car.wheelSize || car.wheel_size || null,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function updateCar(carId, patch) {
  const client = assertSupabase();
  const { data, error } = await client.from("cars").update(patch).eq("id", carId).select().single();
  if (error) throw error;
  return data;
}

export async function deleteCar(carId) {
  const client = assertSupabase();
  const { error } = await client.from("cars").delete().eq("id", carId);
  if (error) throw error;
}

export async function createBooking(userId, booking) {
  const client = assertSupabase();
  const { data, error } = await client.from("bookings").insert({
    user_id: userId,
    business_id: booking.businessId,
    service_id: booking.serviceId || null,
    car_id: booking.carId || null,
    booking_date: booking.bookingDate,
    booking_time: booking.bookingTime,
    comment: booking.comment || null,
    status: "new",
  }).select("*, businesses(name,address), services(name,price_from)").single();
  if (error) throw error;
  return data;
}

export async function loadUserBookings(userId) {
  const client = assertSupabase();
  const { data, error } = await client
    .from("bookings")
    .select("*, businesses(name,address), services(name), cars(brand,model,wheel_size)")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function loadReviews(businessId) {
  if (!isSupabaseConfigured) return fallbackReviews.filter((review) => review.business_id === businessId);
  const { data, error } = await supabase.from("reviews").select("*").eq("business_id", businessId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createReview(userId, review) {
  const client = assertSupabase();
  const { data, error } = await client.from("reviews").insert({
    user_id: userId,
    business_id: review.businessId,
    rating: review.rating,
    text: review.text,
  }).select().single();
  if (error) throw error;
  return data;
}

export async function loadOwnerBusinesses(userId) {
  const client = assertSupabase();
  const { data, error } = await client.from("businesses").select("*, services(*)").eq("owner_id", userId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateBusiness(businessId, patch) {
  const client = assertSupabase();
  const { data, error } = await client.from("businesses").update(patch).eq("id", businessId).select().single();
  if (error) throw error;
  return data;
}

export async function upsertService(service) {
  const client = assertSupabase();
  const { data, error } = await client.from("services").upsert(service).select().single();
  if (error) throw error;
  return data;
}

export async function loadOwnerBookings(businessId) {
  const client = assertSupabase();
  const { data, error } = await client
    .from("bookings")
    .select("*, profiles(full_name,phone), services(name), cars(brand,model,wheel_size)")
    .eq("business_id", businessId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateBookingStatus(bookingId, status) {
  const client = assertSupabase();
  const { data, error } = await client.from("bookings").update({ status }).eq("id", bookingId).select().single();
  if (error) throw error;
  return data;
}

export async function loadAdminData(type = "overview") {
  const client = assertSupabase();
  if (type === "businesses") return (await client.from("businesses").select("*").order("created_at", { ascending: false })).data || [];
  if (type === "bookings") return (await client.from("bookings").select("*, businesses(name), profiles(full_name)").order("created_at", { ascending: false })).data || [];
  if (type === "reviews") return (await client.from("reviews").select("*, businesses(name), profiles(full_name)").order("created_at", { ascending: false })).data || [];
  const [businesses, bookings, reviews] = await Promise.all([
    client.from("businesses").select("id,is_verified,is_active"),
    client.from("bookings").select("id,status"),
    client.from("reviews").select("id"),
  ]);
  for (const result of [businesses, bookings, reviews]) if (result.error) throw result.error;
  return { businesses: businesses.data || [], bookings: bookings.data || [], reviews: reviews.data || [] };
}

export async function verifyBusiness(businessId, isVerified = true) {
  const client = assertSupabase();
  const { data, error } = await client.from("businesses").update({ is_verified: isVerified }).eq("id", businessId).select().single();
  if (error) throw error;
  return data;
}
